import { z } from 'zod';

import { EpmVdpSourceType, type ManagerialAccountingPeriodInput } from '@/common/graphql';

export const MAX_VDP_ENTRIES = 4;
export const MAX_VDD_ENTRIES = 5;
export const MAX_FUNCTIONALITY_ENTRIES = 2;
export const MAX_ROLE_TYPE_ENTRIES = 2;
export const MAX_ALLOCATION_KEY_ENTRIES = 5;

export interface Allocation {
  percentage: number;
}

export interface VdpAllocation extends Allocation {
  sourceType: EpmVdpSourceType | null;
  companyId: string | null;
}

export interface VddAllocation extends Allocation {
  epmMasterVddId: string | null;
}

export interface FunctionalityAllocation extends Allocation {
  epmMasterFunctionalityId: string | null;
}

export interface RoleTypeAllocation extends Allocation {
  epmMasterRoleTypeId: string | null;
}

export interface AllocationKeyAllocation extends Allocation {
  epmMasterAllocationKeyId: string | null;
}

export interface ManagerialAccountingFormData extends ManagerialAccountingFormDataAllocations {
  period: ManagerialAccountingPeriodInput;
}

export interface ManagerialAccountingFormDataAllocations {
  vdp: Array<VdpAllocation>;
  vdd: Array<VddAllocation>;
  functionality: Array<FunctionalityAllocation>;
  roleType: Array<RoleTypeAllocation>;
  allocationKey: Array<AllocationKeyAllocation>;
}

export const percentageField = z.coerce
  .number()
  .refine(v => v > 0, { message: 'Must be > 0' })
  .refine(v => v <= 100, { message: 'Must be ≤ 100' });

const vdpAllocationSchema = z.object({
  sourceType: z.string().nullable(),
  companyId: z.string().nullable(),
  percentage: percentageField,
});

const vddAllocationSchema = z.object({
  epmMasterVddId: z.string().nullable(),
  percentage: percentageField,
});

const functionalityAllocationSchema = z.object({
  epmMasterFunctionalityId: z.string().nullable(),
  percentage: percentageField,
});

const roleTypeAllocationSchema = z.object({
  epmMasterRoleTypeId: z.string().nullable(),
  percentage: percentageField,
});

const allocationKeyAllocationSchema = z.object({
  epmMasterAllocationKeyId: z.string().nullable(),
  percentage: percentageField,
});

export const periodFormSchema = z
  .object({
    period: z.object({ year: z.string(), quarter: z.string() }).passthrough(),
    vdp: z.array(vdpAllocationSchema).max(MAX_VDP_ENTRIES, `At most ${MAX_VDP_ENTRIES} VDP entries allowed`),
    vdd: z.array(vddAllocationSchema).max(MAX_VDD_ENTRIES, `At most ${MAX_VDD_ENTRIES} VDD entries allowed`),
    functionality: z
      .array(functionalityAllocationSchema)
      .min(1, 'At least 1 Functionality entry required')
      .max(MAX_FUNCTIONALITY_ENTRIES, `At most ${MAX_FUNCTIONALITY_ENTRIES} Functionality entries allowed`),
    roleType: z
      .array(roleTypeAllocationSchema)
      .min(1, 'At least 1 Role Type entry required')
      .max(MAX_ROLE_TYPE_ENTRIES, `At most ${MAX_ROLE_TYPE_ENTRIES} Role Type entries allowed`),
    allocationKey: z
      .array(allocationKeyAllocationSchema)
      .max(MAX_ALLOCATION_KEY_ENTRIES, `At most ${MAX_ALLOCATION_KEY_ENTRIES} Allocation Key entries allowed`),
  })
  .superRefine((data, ctx) => {
    // VDP sum = 100
    if (data.vdp.length > 0) {
      const vdpSum = getVdpTotal(data.vdp);
      if (vdpSum !== 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `VDP total is ${vdpSum}% - must be exactly 100%`,
          path: ['vdp'],
        });
      }
    }

    // VDD sum = 100
    if (data.vdd.length > 0) {
      const vddSum = getVddTotal(data.vdd);
      if (vddSum !== 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `VDD total is ${vddSum}% - must be exactly 100%`,
          path: ['vdd'],
        });
      }
    }

    // VDP uniqueness: (sourceType, companyId)
    const vdpKeys = data.vdp.map(v => `${v.sourceType}::${v.companyId ?? ''}`);
    if (new Set(vdpKeys).size < vdpKeys.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Duplicate VDP source (same company, or multiple Regional entries)',
        path: ['vdp'],
      });
    }

    // VDD uniqueness: epmMasterVddId
    const vddIds = data.vdd.map(v => v.epmMasterVddId);
    if (new Set(vddIds).size < vddIds.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Duplicate VDD department',
        path: ['vdd'],
      });
    }

    // VDP sourceType must be selected; COMPANY type must have a companyId
    data.vdp.forEach((v, i) => {
      if (v.sourceType === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Company or 'Regional' is required",
          path: ['vdp', i, 'sourceType'],
        });
      } else if (v.sourceType === EpmVdpSourceType.Company && !v.companyId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Company or 'Regional' is required",
          path: ['vdp', i, 'companyId'],
        });
      }
    });

    // VDD epmMasterVddId must be selected (catches null and empty string '')
    data.vdd.forEach((v, i) => {
      if (!v.epmMasterVddId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Department is required',
          path: ['vdd', i, 'epmMasterVddId'],
        });
      }
    });

    // Functionality sum = 100
    if (data.functionality.length > 0) {
      const sum = getFunctionalityTotal(data.functionality);
      if (sum !== 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Functionality total is ${sum}% - must be exactly 100%`,
          path: ['functionality'],
        });
      }
    }

    // Functionality uniqueness
    const functionalityIds = data.functionality.map(v => v.epmMasterFunctionalityId);
    if (new Set(functionalityIds).size < functionalityIds.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Duplicate Functionality entry',
        path: ['functionality'],
      });
    }

    // Functionality must be selected
    data.functionality.forEach((v, i) => {
      if (!v.epmMasterFunctionalityId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Functionality is required',
          path: ['functionality', i, 'epmMasterFunctionalityId'],
        });
      }
    });

    // RoleType sum = 100
    if (data.roleType.length > 0) {
      const sum = getRoleTypeTotal(data.roleType);
      if (sum !== 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Role Type total is ${sum}% - must be exactly 100%`,
          path: ['roleType'],
        });
      }
    }

    // RoleType uniqueness
    const roleTypeIds = data.roleType.map(v => v.epmMasterRoleTypeId);
    if (new Set(roleTypeIds).size < roleTypeIds.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Duplicate Role Type entry',
        path: ['roleType'],
      });
    }

    // RoleType must be selected
    data.roleType.forEach((v, i) => {
      if (!v.epmMasterRoleTypeId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Role Type is required',
          path: ['roleType', i, 'epmMasterRoleTypeId'],
        });
      }
    });

    // AllocationKey sum = 100
    if (data.allocationKey.length > 0) {
      const sum = getAllocationKeyTotal(data.allocationKey);
      if (sum !== 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Allocation Key total is ${sum}% - must be exactly 100%`,
          path: ['allocationKey'],
        });
      }
    }

    // AllocationKey uniqueness
    const allocationKeyIds = data.allocationKey.map(v => v.epmMasterAllocationKeyId);
    if (new Set(allocationKeyIds).size < allocationKeyIds.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Duplicate Allocation Key entry',
        path: ['allocationKey'],
      });
    }

    // AllocationKey must be selected
    data.allocationKey.forEach((v, i) => {
      if (!v.epmMasterAllocationKeyId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Allocation Key is required',
          path: ['allocationKey', i, 'epmMasterAllocationKeyId'],
        });
      }
    });
  });

export const getVdpTotal = (allocations: Array<z.infer<typeof vdpAllocationSchema>>) =>
  allocations.length === 0
    ? 0
    : allocations.reduce((s, e) => s + (e.sourceType !== null ? Number(e.percentage) || 0 : 0), 0);

export const getVddTotal = (allocations: Array<z.infer<typeof vddAllocationSchema>>) =>
  allocations.length === 0
    ? 0
    : allocations.reduce((s, e) => s + (e.epmMasterVddId !== null ? Number(e.percentage) || 0 : 0), 0);

export const getFunctionalityTotal = (allocations: Array<z.infer<typeof functionalityAllocationSchema>>) =>
  allocations.length === 0
    ? 0
    : allocations.reduce((s, e) => s + (e.epmMasterFunctionalityId !== null ? Number(e.percentage) || 0 : 0), 0);

export const getRoleTypeTotal = (allocations: Array<z.infer<typeof roleTypeAllocationSchema>>) =>
  allocations.length === 0
    ? 0
    : allocations.reduce((s, e) => s + (e.epmMasterRoleTypeId !== null ? Number(e.percentage) || 0 : 0), 0);

export const getAllocationKeyTotal = (allocations: Array<z.infer<typeof allocationKeyAllocationSchema>>) =>
  allocations.length === 0
    ? 0
    : allocations.reduce((s, e) => s + (e.epmMasterAllocationKeyId !== null ? Number(e.percentage) || 0 : 0), 0);

export interface Suggestion {
  percentage: number;
  days?: number;
  suggestionSourceNames: string[];
}

export interface VdpSuggestion extends Suggestion {
  sourceType: EpmVdpSourceType;
  companyId?: string;
  companyName?: string;
}

export interface VddSuggestion extends Suggestion {
  epmMasterVddId: string;
  name: string;
}

export interface FunctionalitySuggestion extends Suggestion {
  epmMasterFunctionalityId: string;
  name: string;
}

export interface RoleTypeSuggestion extends Suggestion {
  epmMasterRoleTypeId: string;
  name: string;
}
